import { useState, useMemo } from "react";
import { format, isAfter, isBefore, startOfToday, endOfWeek, endOfMonth, startOfWeek, startOfMonth, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trash2, Calendar, User, Castle, RefreshCw, Plus, Pencil, Search, ArrowUpDown } from "lucide-react";
import { useReservations, Reservation } from "@/hooks/useReservations";
import { useChateaux } from "@/hooks/useChateaux";
import { useSumos } from "@/hooks/useSumos";
import { toast } from "sonner";

const statusConfig: Record<string, { label: string; color: string }> = {
  en_attente: { label: "En attente", color: "bg-amber-100 text-amber-800" },
  contacte: { label: "Contacté", color: "bg-blue-100 text-blue-800" },
  reserve: { label: "Réservé", color: "bg-emerald-100 text-emerald-800" },
  annule: { label: "Annulé", color: "bg-red-100 text-red-800" },
};

export const ReservationsManager = () => {
  const { reservations, loading, updateStatus, updateReservation, deleteReservation, deleteMultiple, fetchReservations, createReservation } = useReservations();
  const { chateaux } = useChateaux();
  const { sumos } = useSumos();
  const [filterStatus, setFilterStatus] = useState<string>("en_attente");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("date_asc");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [hidePast, setHidePast] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    product_type: "chateau",
    product_id: "",
    product_name: "",
    date_debut: "",
    date_fin: "",
    status: "reserve",
    message: "",
  });
  const [editForm, setEditForm] = useState({
    client_name: "",
    client_email: "",
    client_phone: "",
    product_type: "chateau",
    product_id: "",
    product_name: "",
    date_debut: "",
    date_fin: "",
    status: "en_attente",
    message: "",
  });

  const products = form.product_type === "chateau"
    ? chateaux.map(c => ({ id: c.id, name: c.nom }))
    : sumos.map(s => ({ id: s.id, name: s.title }));

  const editProducts = editForm.product_type === "chateau"
    ? chateaux.map(c => ({ id: c.id, name: c.nom }))
    : sumos.map(s => ({ id: s.id, name: s.title }));

  const handleProductSelect = (productId: string) => {
    const product = products.find(p => p.id === productId);
    setForm(f => ({ ...f, product_id: productId, product_name: product?.name || "" }));
  };

  const handleEditProductSelect = (productId: string) => {
    const product = editProducts.find(p => p.id === productId);
    setEditForm(f => ({ ...f, product_id: productId, product_name: product?.name || "" }));
  };

  const openEdit = (reservation: Reservation) => {
    setEditingReservation(reservation);
    setEditForm({
      client_name: reservation.client_name,
      client_email: reservation.client_email,
      client_phone: reservation.client_phone,
      product_type: reservation.product_type,
      product_id: reservation.product_id || "",
      product_name: reservation.product_name,
      date_debut: reservation.date_debut,
      date_fin: reservation.date_fin,
      status: reservation.status,
      message: reservation.message || "",
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingReservation) return;
    if (!editForm.client_name || !editForm.client_phone || !editForm.product_name || !editForm.date_debut || !editForm.date_fin) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSubmitting(true);
    const success = await updateReservation(editingReservation.id, {
      client_name: editForm.client_name,
      client_email: editForm.client_email,
      client_phone: editForm.client_phone,
      product_type: editForm.product_type,
      product_id: editForm.product_id || null,
      product_name: editForm.product_name,
      date_debut: editForm.date_debut,
      date_fin: editForm.date_fin,
      status: editForm.status,
      message: editForm.message || null,
    });
    setSubmitting(false);
    if (success) {
      setEditOpen(false);
      setEditingReservation(null);
    }
  };

  const handleSubmitManual = async () => {
    if (!form.client_name || !form.client_phone || !form.product_name || !form.date_debut || !form.date_fin) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSubmitting(true);
    const success = await createReservation({
      client_name: form.client_name,
      client_email: form.client_email || "direct@telephone.com",
      client_phone: form.client_phone,
      product_type: form.product_type,
      product_id: form.product_id || null,
      product_name: form.product_name,
      date_debut: form.date_debut,
      date_fin: form.date_fin,
      status: form.status,
      message: form.message || null,
    });
    setSubmitting(false);
    if (success) {
      toast.success("Réservation ajoutée avec succès");
      setAddOpen(false);
      setForm({ client_name: "", client_email: "", client_phone: "", product_type: "chateau", product_id: "", product_name: "", date_debut: "", date_fin: "", status: "reserve", message: "" });
      fetchReservations();
    } else {
      toast.error("Erreur lors de l'ajout");
    }
  };

  const today = startOfToday();

  // Filter by status
  let filtered = filterStatus === "all" 
    ? reservations 
    : reservations.filter(r => r.status === filterStatus);

  // Hide past reservations
  if (hidePast) {
    filtered = filtered.filter(r => {
      try {
        return !isBefore(new Date(r.date_fin), today);
      } catch {
        return true;
      }
    });
  }

  // Search filter
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase().trim();
    filtered = filtered.filter(r =>
      r.client_name.toLowerCase().includes(q) ||
      r.client_email.toLowerCase().includes(q) ||
      r.client_phone.toLowerCase().includes(q) ||
      r.product_name.toLowerCase().includes(q)
    );
  }

  // Filter by date period
  if (dateFrom) {
    filtered = filtered.filter(r => r.date_debut >= dateFrom);
  }
  if (dateTo) {
    filtered = filtered.filter(r => r.date_fin <= dateTo);
  }

  // Sort
  filtered = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case "name_asc": return a.client_name.localeCompare(b.client_name);
      case "name_desc": return b.client_name.localeCompare(a.client_name);
      case "date_asc": return a.date_debut.localeCompare(b.date_debut);
      case "date_desc": return b.date_debut.localeCompare(a.date_debut);
      case "created_asc": return a.created_at.localeCompare(b.created_at);
      case "created_desc": return b.created_at.localeCompare(a.created_at);
      default: return 0;
    }
  });

  // Group by period
  const grouped = useMemo(() => {
    const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
    const monthEnd = endOfMonth(today);

    const groups: { label: string; items: typeof filtered }[] = [];
    const thisWeek: typeof filtered = [];
    const thisMonth: typeof filtered = [];
    const later: typeof filtered = [];
    const past: typeof filtered = [];

    for (const r of filtered) {
      try {
        const d = new Date(r.date_debut);
        if (isBefore(d, today)) {
          past.push(r);
        } else if (!isAfter(d, weekEnd)) {
          thisWeek.push(r);
        } else if (!isAfter(d, monthEnd)) {
          thisMonth.push(r);
        } else {
          later.push(r);
        }
      } catch {
        later.push(r);
      }
    }

    if (thisWeek.length > 0) groups.push({ label: "📅 Cette semaine", items: thisWeek });
    if (thisMonth.length > 0) groups.push({ label: "📆 Ce mois-ci", items: thisMonth });
    if (later.length > 0) groups.push({ label: "🔮 À venir", items: later });
    if (past.length > 0) groups.push({ label: "⏳ Passées", items: past });

    return groups;
  }, [filtered, today]);

  const counts = {
    all: reservations.length,
    en_attente: reservations.filter(r => r.status === 'en_attente').length,
    contacte: reservations.filter(r => r.status === 'contacte').length,
    reserve: reservations.filter(r => r.status === 'reserve').length,
    annule: reservations.filter(r => r.status === 'annule').length,
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(r => r.id)));
    }
  };

  const handleMassDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Supprimer ${selectedIds.size} réservation(s) ?`)) return;
    await deleteMultiple(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleMassStatusChange = async (newStatus: string) => {
    if (selectedIds.size === 0) return;
    const statusLabel = statusConfig[newStatus]?.label || newStatus;
    if (!confirm(`Changer le statut de ${selectedIds.size} réservation(s) en "${statusLabel}" ?`)) return;
    for (const id of selectedIds) {
      await updateStatus(id, newStatus);
    }
    setSelectedIds(new Set());
    toast.success(`${selectedIds.size} réservation(s) mises à jour en "${statusLabel}"`);
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Chargement des réservations...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl font-display font-semibold">Gestion des Réservations</h3>
          <p className="text-sm text-muted-foreground">{reservations.length} réservation(s) au total</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Ajout manuel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Ajouter une réservation manuelle</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Nom du client *</Label>
                    <Input value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} placeholder="Nom complet" />
                  </div>
                  <div>
                    <Label>Téléphone *</Label>
                    <Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} placeholder="0470..." />
                  </div>
                </div>
                <div>
                  <Label>Email (optionnel)</Label>
                  <Input value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} placeholder="email@exemple.com" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type de produit</Label>
                    <Select value={form.product_type} onValueChange={v => setForm(f => ({ ...f, product_type: v, product_id: "", product_name: "" }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chateau">Château</SelectItem>
                        <SelectItem value="sumo">Sumo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Produit *</Label>
                    <Select value={form.product_id} onValueChange={handleProductSelect}>
                      <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                      <SelectContent>
                        {products.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Date début *</Label>
                    <Input type="date" value={form.date_debut} onChange={e => setForm(f => ({ ...f, date_debut: e.target.value }))} />
                  </div>
                  <div>
                    <Label>Date fin *</Label>
                    <Input type="date" value={form.date_fin} onChange={e => setForm(f => ({ ...f, date_fin: e.target.value }))} />
                  </div>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="en_attente">En attente</SelectItem>
                      <SelectItem value="contacte">Contacté</SelectItem>
                      <SelectItem value="reserve">Réservé</SelectItem>
                      <SelectItem value="annule">Annulé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Message / Note</Label>
                  <Textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} placeholder="Réservation par téléphone..." rows={2} />
                </div>
                <Button onClick={handleSubmitManual} disabled={submitting} className="w-full">
                  {submitting ? "Ajout en cours..." : "Ajouter la réservation"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="sm" onClick={() => fetchReservations()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Search + Sort + Date Filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-sm font-medium text-foreground mb-1 block">Recherche</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Nom, email, téléphone, produit..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="w-52">
          <Label className="text-sm font-medium text-foreground mb-1 block">Trier par</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="bg-background text-foreground">
              <ArrowUpDown className="w-3 h-3 mr-1 flex-shrink-0" />
              <SelectValue placeholder="Trier par..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="date_asc">Date événement ↑</SelectItem>
              <SelectItem value="date_desc">Date événement ↓</SelectItem>
              <SelectItem value="created_desc">Date création ↓</SelectItem>
              <SelectItem value="created_asc">Date création ↑</SelectItem>
              <SelectItem value="name_asc">Nom A → Z</SelectItem>
              <SelectItem value="name_desc">Nom Z → A</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm font-medium text-foreground mb-1 block">Période du</Label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-44" />
        </div>
        <div>
          <Label className="text-sm font-medium text-foreground mb-1 block">Au</Label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-44" />
        </div>
        {(dateFrom || dateTo || searchQuery) && (
          <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); setSearchQuery(""); }}>
            Effacer
          </Button>
        )}
      </div>

      {/* Status Filter Tabs + Hide Past Toggle */}
      <div className="flex flex-wrap items-center gap-2">
        {[
          { key: "all", label: "Toutes" },
          { key: "en_attente", label: "En attente" },
          { key: "contacte", label: "Contacté" },
          { key: "reserve", label: "Réservé" },
          { key: "annule", label: "Annulé" },
        ].map(({ key, label }) => (
          <Button
            key={key}
            variant={filterStatus === key ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterStatus(key)}
            className={filterStatus === key ? "" : "border-border text-foreground hover:bg-accent"}
          >
            {label}
            <Badge variant="secondary" className="ml-2 text-xs px-1.5 py-0.5 min-w-[20px] text-center">
              {counts[key as keyof typeof counts]}
            </Badge>
          </Button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <Checkbox
            id="hide-past"
            checked={hidePast}
            onCheckedChange={(checked) => setHidePast(checked === true)}
          />
          <label htmlFor="hide-past" className="text-sm text-foreground cursor-pointer">
            Masquer les passées
          </label>
        </div>
      </div>

      {/* Mass Actions */}
      {filtered.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={selectedIds.size === filtered.length && filtered.length > 0}
              onCheckedChange={toggleSelectAll}
            />
            <span className="text-sm text-muted-foreground">
              {selectedIds.size > 0 ? `${selectedIds.size} sélectionnée(s)` : "Tout sélectionner"}
            </span>
          </div>
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2">
              <Select onValueChange={handleMassStatusChange}>
                <SelectTrigger className="w-48 h-9">
                  <SelectValue placeholder="Changer le statut..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="contacte">Contacté</SelectItem>
                  <SelectItem value="reserve">Réservé</SelectItem>
                  <SelectItem value="annule">Annulé</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="destructive" size="sm" onClick={handleMassDelete}>
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer ({selectedIds.size})
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Reservations List - Grouped */}
      {filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">Aucune réservation pour ce filtre.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {grouped.map((group) => (
            <div key={group.label} className="space-y-3">
              <div className="flex items-center gap-2">
                <h4 className="text-base font-semibold text-foreground">{group.label}</h4>
                <Badge variant="outline" className="text-xs">{group.items.length}</Badge>
              </div>
              <div className="grid gap-3">
                {group.items.map((reservation) => (
                  <ReservationCard
                    key={reservation.id}
                    reservation={reservation}
                    selected={selectedIds.has(reservation.id)}
                    onToggleSelect={() => toggleSelect(reservation.id)}
                    onStatusChange={updateStatus}
                    onDelete={deleteReservation}
                    onEdit={openEdit}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier la réservation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nom du client *</Label>
                <Input value={editForm.client_name} onChange={e => setEditForm(f => ({ ...f, client_name: e.target.value }))} />
              </div>
              <div>
                <Label>Téléphone *</Label>
                <Input value={editForm.client_phone} onChange={e => setEditForm(f => ({ ...f, client_phone: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Email</Label>
              <Input value={editForm.client_email} onChange={e => setEditForm(f => ({ ...f, client_email: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type de produit</Label>
                <Select value={editForm.product_type} onValueChange={v => setEditForm(f => ({ ...f, product_type: v, product_id: "", product_name: "" }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="chateau">Château</SelectItem>
                    <SelectItem value="sumo">Sumo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Produit *</Label>
                <Select value={editForm.product_id} onValueChange={handleEditProductSelect}>
                  <SelectTrigger><SelectValue placeholder="Choisir..." /></SelectTrigger>
                  <SelectContent>
                    {editProducts.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Date début *</Label>
                <Input type="date" value={editForm.date_debut} onChange={e => setEditForm(f => ({ ...f, date_debut: e.target.value }))} />
              </div>
              <div>
                <Label>Date fin *</Label>
                <Input type="date" value={editForm.date_fin} onChange={e => setEditForm(f => ({ ...f, date_fin: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en_attente">En attente</SelectItem>
                  <SelectItem value="contacte">Contacté</SelectItem>
                  <SelectItem value="reserve">Réservé</SelectItem>
                  <SelectItem value="annule">Annulé</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message / Note</Label>
              <Textarea value={editForm.message} onChange={e => setEditForm(f => ({ ...f, message: e.target.value }))} rows={2} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)} className="flex-1">Annuler</Button>
              <Button onClick={handleSaveEdit} disabled={submitting} className="flex-1">
                {submitting ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface ReservationCardProps {
  reservation: Reservation;
  selected: boolean;
  onToggleSelect: () => void;
  onStatusChange: (id: string, status: string) => void;
  onDelete: (id: string) => void;
  onEdit: (reservation: Reservation) => void;
}

const ReservationCard = ({ reservation, selected, onToggleSelect, onStatusChange, onDelete, onEdit }: ReservationCardProps) => {
  const config = statusConfig[reservation.status] || statusConfig.en_attente;

  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "dd MMM yyyy", { locale: fr });
    } catch {
      return dateStr;
    }
  };

  const borderColor = reservation.status === 'reserve'
    ? 'hsl(var(--primary))' 
    : reservation.status === 'annule' 
      ? 'hsl(var(--destructive))' 
      : 'hsl(var(--accent))';

  return (
    <Card className="hover:shadow-md transition-shadow border-l-4" style={{ borderLeftColor: borderColor }}>
      <CardContent className="p-4">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          {/* Checkbox + Info */}
          <div className="flex items-start gap-3 flex-1">
            <Checkbox
              checked={selected}
              onCheckedChange={onToggleSelect}
              className="mt-1"
            />
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {/* Client */}
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-semibold text-sm truncate">{reservation.client_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{reservation.client_email}</p>
                  <p className="text-xs text-muted-foreground">{reservation.client_phone}</p>
                </div>
              </div>

              {/* Product */}
              <div className="flex items-center gap-2">
                <Castle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">{reservation.product_name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{reservation.product_type}</p>
                </div>
              </div>

              {/* Dates */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="text-sm">{formatDate(reservation.date_debut)}</p>
                  <p className="text-xs text-muted-foreground">→ {formatDate(reservation.date_fin)}</p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="flex items-center">
                <Badge className={config.color}>
                  {config.label}
                </Badge>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Select
              value={reservation.status}
              onValueChange={(value) => onStatusChange(reservation.id, value)}
            >
              <SelectTrigger className="w-[140px] bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background border shadow-lg z-50">
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="contacte">Contacté</SelectItem>
                <SelectItem value="reserve">Réservé</SelectItem>
                <SelectItem value="annule">Annulé</SelectItem>
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="icon"
              onClick={() => onEdit(reservation)}
              title="Modifier"
            >
              <Pencil className="w-4 h-4" />
            </Button>

            <Button
              variant="destructive"
              size="icon"
              onClick={() => {
                if (confirm(`Supprimer la réservation de ${reservation.client_name} ?`)) {
                  onDelete(reservation.id);
                }
              }}
              title="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Message */}
        {reservation.message && (
          <div className="mt-3 pt-3 border-t">
            <p className="text-sm text-muted-foreground italic">"{reservation.message}"</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
